import { Box, Button, Grid2, Paper, Stack, Typography, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUnit, fetchViolationComments, singleViolation, statusUpdateViolationAPIStatus } from '../../Redux/selectors';
import { Unit, Violation, ViolationType, ViolationStage, ViolationStatus, User, UserType, APICallStatus } from '../../Types/UnitFeatureTypes';
import UnitCard from '../CommonComponents/UnitCard';
import CalendarWithDate from '../CommonComponents/CalenderWithDate';
import CommentSection from './CommentSection';
import { CrisisAlert, Delete, Done, Edit, Send } from '@mui/icons-material';
import WarningDialog from '../CommonComponents/WarningDialog';
import { useAuth } from '../../context/authContext';
import Editor from '../TextEditor/Editor';
import UserCard from '../CommonComponents/UserCard';
import { Actions } from '../../Redux/Reducer/rootReducer';
import { useApp } from '../../context/appContext';

const ViolationView: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { user} = useAuth();
    const IS_MANAGER = user?.type === UserType.MANAGER;

    const unitList = useSelector(fetchUnit);
    const currentRequestData = useSelector(singleViolation) as Violation;
    const violationComments = useSelector(fetchViolationComments);
    const [openWarningDialog, setOpenWarningDialog] = React.useState(false);
    const userWithName = { ...user, name: user?.first_name + " "+ user.last_name || 'Unknown' };
    const [violationData, setViolationData] = React.useState<Violation>({
        id: 0,
        title: '',
        description: '',
        possible_penalties: '',
        created_on: '',
        updated_on: '',
        violation_date: new Date(),
        due_date: undefined,
        unit: {
            id: 0,
            unit_number: '',
        },
        type: ViolationType.GENERAL,
        stage: ViolationStage.FIRST_VIOLATION,
        resolution_date: undefined,
        status: ViolationStatus.OPEN,
    });
    const violationUnit: Unit | undefined =  unitList.find((unitData) => unitData.id === violationData.unit.id) || undefined;

    const statusUpdateApi = useSelector(statusUpdateViolationAPIStatus);
    const { setSnackbar } = useApp();


    useEffect(() => {
        if (statusUpdateApi === APICallStatus.SUCCESS) {
            setSnackbar(true, 'Violation status updated successfully', 'success');
            dispatch(Actions.fetchViolation.violationActions.request({
                id: violationData.id.toString()
            }));
        } else if (statusUpdateApi === APICallStatus.FAILURE) {
            setSnackbar(true, 'Failed to update violation status', 'error');
        }
        dispatch(Actions.violation.resetStatusRequestAction());
    }, [statusUpdateApi]);

    useEffect(() => {
        if (location.state && location.state.id) {
            dispatch(Actions.fetchViolation.violationActions.request({id: location.state.id}));
            dispatch(Actions.fetchViolation.violationCommentActions.request({id: location.state.id}));
        }
    }, []);

    useEffect(() => {
        if (currentRequestData && !Array.isArray(currentRequestData)) {
            setViolationData({
                id: currentRequestData.id,
                title: currentRequestData.title,
                description: currentRequestData.description,
                possible_penalties: currentRequestData.possible_penalties,
                created_on: currentRequestData.created_on,
                updated_on: currentRequestData.updated_on,
                violation_date: currentRequestData.violation_date,
                due_date: currentRequestData.due_date,
                unit: currentRequestData.unit,
                type: currentRequestData.type,
                stage: currentRequestData.stage,
                resolution_date: currentRequestData.resolution_date,
                status: currentRequestData.status,
            })
        }
    }, [currentRequestData]);

    const handleDeleteDraft = () => {
        setOpenWarningDialog(true);
    }
    const handleEditDraft = () => {
        navigate('/createViolation', { state: { edit: true, violation: violationData  } }); 
    }

    const handleResolveViolation = () => {
        dispatch(Actions.violation.updateStatusViolationRequestAction.request({ id: violationData.id, status: ViolationStatus.RESOLVED  }));
    }

    const theme = useTheme();

    const handleSendDraft = () => {
        dispatch(Actions.violation.updateStatusViolationRequestAction.request({ id: violationData.id, status: ViolationStatus.OPEN  }));
    }

    return (
        <>
        <WarningDialog 
            open={openWarningDialog}
            title="Delete Violation Draft"
            message="Are you sure you want to delete this draft?"
            onConfirm={() => {}}
            onClose={() => setOpenWarningDialog(false)}
        />
        <Paper elevation={3} sx={{mb: 2, padding: 2, borderRadius: 3, display: 'flex', justifyContent: 'space-between'}}>
            <Box color='primary.main' sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                <CrisisAlert sx={{mr:1, height: 40, width: 40}} />
                <Typography variant="h5">Violation</Typography>
            </Box>
        </Paper>
        <Paper elevation={5} sx={{padding: 4, m: 1, borderRadius: 3}}>
            <Typography variant="h4" gutterBottom>{violationData.title} Violation</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <UserCard user={userWithName} isOwner={true} />
                <UnitCard unit={violationUnit} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, mt: 5 }}>
                <Typography variant="h6" color="textSecondary">Violation Date: {new Date(violationData?.violation_date).toDateString()}</Typography>
                <Typography variant="h6" color="textSecondary">Created On: {new Date(violationData?.created_on).toDateString()}</Typography>
            </Box>
            <Typography variant="h6" color="textSecondary">Status: {violationData?.status}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, mt: 5 }}>
                <Stack>
                    <Typography variant="h6" color="textSecondary" gutterBottom>Violation Date</Typography>
                    <CalendarWithDate 
                        start={new Date(violationData.violation_date)}
                        end={new Date(violationData.violation_date)}
                        color={theme.palette.primary.main}
                    />
                </Stack>
                <Stack>
                    <Typography variant="h6" color="textSecondary" gutterBottom>Due Date</Typography>
                    {violationData.due_date ? <CalendarWithDate 
                        start={new Date(violationData.due_date)}
                        end={new Date(violationData.due_date)}
                    /> : <Paper sx={{p: 4, borderRadius: 3, border: `2px solid ${theme.palette.primary.main}` }}><Typography variant="h6" color="textSecondary">No Due Date</Typography></Paper>}
                </Stack>
            </Box>

            <Typography mt={4} variant="h6" color="textSecondary" gutterBottom>Possible Penalties</Typography>
            <Paper sx={{p: 4, borderRadius: 3, border: `2px solid ${theme.palette.primary.main}` }}>
                <Typography variant="body1">{violationData.possible_penalties}</Typography>
            </Paper>

            <Typography mt={4} variant="h6" color="textSecondary" gutterBottom>Description</Typography>
            <Editor 
                value={violationData.description}
                isReadOnly={true}
                placeholder="No Description"
                onChange={() => {}}
                error={false}
            />

            <Grid2 container sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 2 }} spacing={1}>
 
                {IS_MANAGER && <>
                {violationData.status === ViolationStatus.DRAFT && <>
                    <Grid2 size={{xs: 6, sm: 6, md: 3}}>
                        <Button 
                            onClick={handleDeleteDraft}
                            sx={{width: '100%', '&:hover': {backgroundColor: 'error.main'} }} variant="contained" color="primary">
                                <Delete sx={{mr: 2}} /> Delete
                        </Button>
                    </Grid2>
                    <Grid2 size={{xs: 6, sm: 6, md: 3}}>
                        <Button 
                            onClick={handleEditDraft}
                            sx={{width: '100%', '&:hover': {backgroundColor: 'secondary.main'}}} variant="contained" color="primary">
                                <Edit sx={{mr: 2}} /> Edit
                        </Button>
                    </Grid2>
                    <Grid2 size={{xs: 6, sm: 6, md: 3}}>
                        <Button 
                            onClick={handleSendDraft}
                            sx={{width: '100%', '&:hover': {backgroundColor: 'success.main'}}} variant="contained" color="primary">
                                <Send sx={{mr: 2}} /> Send
                        </Button>
                    </Grid2>
                </>}
                {(violationData.status === ViolationStatus.OWNER_ACTION_REQUIRED || violationData.status === ViolationStatus.OPEN)  && <Grid2 sx={{flexGrow: 1 }} size={{xs: 6, md: 3}}>
                    <Button onClick={handleResolveViolation} sx={{width: '100%', '&:hover': {backgroundColor: 'success.main'}}} variant="contained" color="primary"><Done sx={{mr: 2}} /> Resolve</Button>
                </Grid2>}
                </>}
            </Grid2>
        </Paper>
        <Paper elevation={5} sx={{padding: 4, m: 1, borderRadius: 3}}>
        <Typography variant="h5" gutterBottom>Comments</Typography>
        <CommentSection previousComments={violationComments}/>
    </Paper>
    </>
            
    );
}

export default ViolationView;